import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { auth, FIRESTORE_COLLECTIONS, firestore } from '../firebase';
import en from './en.js';
import de from './de.js';
import es from './es.js';
import fr from './fr.js';
import ru from './ru.js';
import { selectPluralCategory } from './pluralRules';

export const SUPPORTED_LANGUAGES = ['en', 'de', 'es', 'fr', 'ru'] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  en: 'English',
  de: 'Deutsch',
  es: 'Español',
  fr: 'Français',
  ru: 'Русский',
};

export const LANGUAGE_LOCALES: Record<SupportedLanguage, string> = {
  en: 'en-GB',
  de: 'de-DE',
  es: 'es-ES',
  fr: 'fr-FR',
  ru: 'ru-RU',
};

type TranslationTree = Record<string, unknown>;
const DICTS: Record<SupportedLanguage, TranslationTree> = { en, de, es, fr, ru };
const LANGUAGE_STORAGE_KEY = '@tonyte/preferred-language';
let activeLanguage: SupportedLanguage = 'en';

type I18nContextValue = {
  language: SupportedLanguage;
  setLanguage: (language: SupportedLanguage) => Promise<void>;
  t: (key: string, fallback?: string) => string;
  tp: (key: string, count: number, fallback?: string) => string;
};

const I18nContext = createContext<I18nContextValue>({
  language: 'en',
  setLanguage: async () => undefined,
  t: (_key, fallback) => fallback || _key,
  tp: (_key, count, fallback) => (fallback || _key).replace('{count}', String(count)),
});

function isSupportedLanguage(value: unknown): value is SupportedLanguage {
  return typeof value === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

function lookup(dict: TranslationTree, key: string): string | undefined {
  const value = key.split('.').reduce<unknown>((current, part) => {
    if (!current || typeof current !== 'object') return undefined;
    return (current as Record<string, unknown>)[part];
  }, dict);
  return typeof value === 'string' ? value : undefined;
}

export function translateCurrent(key: string, fallback?: string): string {
  return lookup(DICTS[activeLanguage], key) ?? lookup(DICTS.en, key) ?? fallback ?? key;
}

export function translatePluralCurrent(key: string, count: number, fallback?: string): string {
  const category = selectPluralCategory(activeLanguage, count);
  const pluralKey = `${key}_${category}`;
  const otherKey = `${key}_other`;
  const template = lookup(DICTS[activeLanguage], pluralKey)
    ?? lookup(DICTS[activeLanguage], otherKey)
    ?? lookup(DICTS.en, pluralKey)
    ?? lookup(DICTS.en, otherKey)
    ?? fallback
    ?? key;
  return template.replace(/\{count\}/g, String(count));
}

export function getCurrentLocale(): string {
  return LANGUAGE_LOCALES[activeLanguage];
}

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const uid = auth.currentUser?.uid ?? null;
  const [language, setLanguageState] = useState<SupportedLanguage>('en');

  useEffect(() => {
    let active = true;
    const restoreStoredLanguage = async () => {
      const stored = await AsyncStorage.getItem(LANGUAGE_STORAGE_KEY).catch(() => null);
      if (active && isSupportedLanguage(stored)) {
        activeLanguage = stored;
        setLanguageState(stored);
      }
    };

    if (!uid) {
      restoreStoredLanguage();
      return () => { active = false; };
    }
    const unsubscribe = onSnapshot(
      doc(firestore, FIRESTORE_COLLECTIONS.users, uid),
      snapshot => {
        const preferredLanguage = snapshot.data()?.preferredLanguage;
        if (isSupportedLanguage(preferredLanguage)) {
          activeLanguage = preferredLanguage;
          setLanguageState(preferredLanguage);
          AsyncStorage.setItem(LANGUAGE_STORAGE_KEY, preferredLanguage).catch(() => undefined);
        } else {
          restoreStoredLanguage();
        }
      },
      restoreStoredLanguage,
    );
    return () => {
      active = false;
      unsubscribe();
    };
  }, [uid]);

  const setLanguage = useCallback(async (nextLanguage: SupportedLanguage) => {
    activeLanguage = nextLanguage;
    setLanguageState(nextLanguage);
    await AsyncStorage.setItem(LANGUAGE_STORAGE_KEY, nextLanguage);
    const currentUid = auth.currentUser?.uid;
    if (!currentUid) return;
    await setDoc(
      doc(firestore, FIRESTORE_COLLECTIONS.users, currentUid),
      { preferredLanguage: nextLanguage, updatedAt: serverTimestamp() },
      { merge: true },
    );
  }, []);

  const t = useCallback((key: string, fallback?: string) => {
    return lookup(DICTS[language], key) ?? lookup(DICTS.en, key) ?? fallback ?? key;
  }, [language]);

  const tp = useCallback((key: string, count: number, fallback?: string) => {
    const category = selectPluralCategory(language, count);
    const pluralKey = `${key}_${category}`;
    const otherKey = `${key}_other`;
    const template = lookup(DICTS[language], pluralKey)
      ?? lookup(DICTS[language], otherKey)
      ?? lookup(DICTS.en, pluralKey)
      ?? lookup(DICTS.en, otherKey)
      ?? fallback
      ?? key;
    return template.replace(/\{count\}/g, String(count));
  }, [language]);

  const value = useMemo(() => ({ language, setLanguage, t, tp }), [language, setLanguage, t, tp]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export function useI18n() {
  return useContext(I18nContext);
}
